<template>
  <div class="page-container">
    <div id="mescroll" class="mescroll">
      <div class="page-content">
        <!-- 筛选区域 -->
        <div class="filter-section">
        <van-cell-group>
          <!-- 学生姓名 -->
          <van-field
            v-if="!state.stuId"
            v-model="state.filterForm.stuName"
            label="学生姓名"
            placeholder="请输入学生姓名"
            clearable
            @clear="handleSearch"
          />
          
          <!-- 沟通日期 -->
          <van-field
            :model-value="dateRangeText"
            label="沟通日期"
            placeholder="请选择日期范围"
            readonly
            is-link
            @click="showDatePicker = true"
          >
            <template #right-icon>
              <van-icon 
                v-if="dateRangeText" 
                name="cross" 
                class="clear-icon"
                @click.stop="handleClearDate"
              />
            </template>
          </van-field>
          <van-calendar
            v-model:show="showDatePicker"
            type="range"
            @confirm="onDateConfirm"
            :min-date="minDate"
            :max-date="maxDate"
            allow-same-day
          />
          
          <!-- 沟通人 -->
          <van-field
            v-model="state.filterForm.contactUserName"
            label="沟通人"
            placeholder="请输入沟通人"
            clearable
            @clear="handleSearch"
          />
        </van-cell-group>
        
        <!-- 搜索按钮 -->
        <div class="filter-actions">
          <van-button 
            type="primary" 
            block
            class="search-btn"
            @click="handleSearch"
            :loading="state.requestLoading"
          >
            搜索
          </van-button>
          <van-button 
            plain 
            type="primary" 
            block
            class="reset-btn"
            @click="handleReset"
          >
            重置
          </van-button>
        </div>
      </div>

      <!-- 记录列表 -->
      <div class="content-list">
        <div 
          class="content-item" 
          v-for="item in state.list" 
          :key="item.id"
          @click="handleGoDetail(item)"
        >
          <!-- 头部信息：学生姓名和时间 -->
          <div class="item-header">
            <span class="header-name">{{ item.stuName || '--' }}</span>
            <span class="header-time">{{ formatDate(item.contactTime) }}</span>
          </div>
          
          <!-- 主要内容区域 -->
          <div class="item-content">
            <!-- 沟通人 -->
            <div class="info-row" v-if="item.contactUserName">
              <span class="info-label">沟通人：</span>
              <span class="info-value highlighted">{{ item.contactUserName }}</span>
            </div>
            
            <!-- 沟通类型 -->
            <div class="info-row" v-if="item.typeName">
              <span class="info-label">沟通类型：</span>
              <span class="info-value">{{ item.typeName }}</span>
            </div>

            <!-- 沟通内容 -->
            <div class="info-row" v-if="item.content">
              <span class="info-label">沟通内容：</span>
              <div class="info-value">{{ item.content }}</div>
            </div>
            
            <!-- 图片展示 -->
            <div class="item-images" v-if="getImageList(item.images).length > 0">
              <div class="images-list" @click.stop="handlePreviewImages(item.images, $event)">
                <div 
                  class="image-item" 
                  v-for="(img, imgIndex) in getImageList(item.images)" 
                  :key="imgIndex"
                >
                  <van-image 
                    :src="getImageUrl(img)" 
                    fit="cover"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
        
        <EmptyData v-if="!state.list.length && !state.requestLoading && !state.isFirstLoad" />
      </div>
      
      <!-- 图片预览 -->
      <van-image-preview 
        v-model:show="state.showImagePreview" 
        :images="state.previewImages"
        :start-position="state.startPosition"
        @close="state.showImagePreview = false"
      />
      </div>
    </div>
  </div>
</template>

<script>
  import { defineComponent, onMounted, reactive, ref, nextTick, computed } from 'vue';
  import { useRoute, useRouter } from "vue-router";
  import EmptyData from '@/components/EmptyData.vue';
  import dayjs from "dayjs";
  import { queryCommunicationRecords as queryCommunicationRecordsApi } from "@/api/user";

  export default defineComponent({
    name: 'CommunicationRecords',
    
    components: {
      EmptyData
    },

    setup() {
      const route = useRoute();
      const router = useRouter();
      const showDatePicker = ref(false);
      const pageSize = ref(10);
      let mescroll = null;

        const state = reactive({
          list: [],
          filterForm: {
            stuName: '',
            startDate: '',
            endDate: '',
            contactUserName: ''
          },
        requestLoading: false,
        isFirstLoad: true,
        pageNum: 1,
        oneSelf: route?.query?.oneSelf,
        permissionOrg: route?.query?.permissionOrg,
        stuId: route?.query?.userId,
        showImagePreview: false,
        previewImages: [],
        startPosition: 0
      });

      onMounted(() => {
        // 默认查询今日的沟通记录
        // 初始化 mescroll
        nextTick(() => {
          mescroll = new MeScroll('mescroll', {
            down: {
              auto: false, // 不自动下拉刷新
              mustToTop: false, // 允许任何时候下拉刷新
              callback: downCallback
            },
            up: {
              callback: upCallback,
              page: {
                num: 1,
                size: pageSize.value
              }
            }
          });
        });
      });

      // 下拉刷新的回调
      const downCallback = async () => {
        state.pageNum = 1;
        state.isFirstLoad = true;
        await queryCommunicationRecords(true, mescroll);
      };

      // 上拉加载的回调
      const upCallback = async () => {
        await queryCommunicationRecords(false, mescroll);
      };

      const queryCommunicationRecords = async (isRefresh = false, mescrollInstance = null) => {
        // 使用当前页号
        const currentPage = state.pageNum;
        
        state.requestLoading = true;
        try {
          const params = {
            stuName: state.filterForm.stuName,
            startDate: state.filterForm.startDate,
            endDate: state.filterForm.endDate,
            contactUserName: state.filterForm.contactUserName,
            oneSelf: state.oneSelf,
            permissionOrg: state.permissionOrg,
            pageSize: pageSize.value,
            pageNum: currentPage,
          }

          if(state.stuId) {
            params.stuIdList = [state.stuId];
          }
          
          const result = await queryCommunicationRecordsApi({...params});
          
          console.log("queryCommunicationRecords result:", result);
          
          const data = result.data?.records || result.data?.list || result.data || [];
          const curPageData = data || [];
          const curPageLen = curPageData.length;
          
          if (result.code === "00") {
            if (isRefresh || currentPage === 1) {
              // 下拉刷新或首次加载
              state.list = curPageData;
            } else {
              // 追加数据
              state.list = state.list.concat(curPageData);
            }
            
            // 更新分页信息
            state.pageNum++;
            
            // 判断是否还有更多数据
            if (curPageLen < pageSize.value) {
              // 没有更多数据了
              if (mescrollInstance) {
                mescrollInstance.endByPage(curPageLen, pageSize.value);
              }
            } else {
              // 还有数据
              if (mescrollInstance) {
                mescrollInstance.endBySize(curPageLen, pageSize.value);
              }
            }
          } else {
            if (isRefresh || currentPage === 1) {
              state.list = [];
            }
            if (mescrollInstance) {
              mescrollInstance.endErr();
            }
          }
          
          state.isFirstLoad = false;
        } catch (error) {
          console.log("error:>>>", error);
          if (isRefresh || currentPage === 1) {
            state.list = [];
          }
          if (mescrollInstance) {
            mescrollInstance.endErr();
          }
        } finally {
          state.requestLoading = false;
          if (mescrollInstance) {
            mescrollInstance.endSuccess();
          }
        }
      };

      const handleSearch = () => {
        state.pageNum = 1;
        state.isFirstLoad = true;
        if (mescroll) {
          mescroll.resetUpScroll();
        }
      };

      const handleReset = () => {
        state.filterForm = {
          stuName: '',
          startDate: dayjs().format('YYYY-MM-DD'),
          endDate: dayjs().format('YYYY-MM-DD'),
          contactUserName: ''
        };
        state.pageNum = 1;
        state.isFirstLoad = true;
        if (mescroll) {
          mescroll.resetUpScroll();
        }
      };

      const dateRangeText = computed(() => {
        if (!state.filterForm.startDate && !state.filterForm.endDate) {
          return '';
        }
        if (state.filterForm.startDate == state.filterForm.endDate) {
          return state.filterForm.startDate;
        }
        return `${state.filterForm.startDate} 至 ${state.filterForm.endDate}`;
      });

      const onDateConfirm = (values) => {
        if (Array.isArray(values) && values.length === 2) {
          state.filterForm.startDate = dayjs(values[0]).format('YYYY-MM-DD');
          state.filterForm.endDate = dayjs(values[1]).format('YYYY-MM-DD');
        }
        showDatePicker.value = false;
      };

      const handleClearDate = () => {
        state.filterForm.startDate = '';
        state.filterForm.endDate = '';
      };

      const formatDate = (date) => {
        return date ? dayjs(date).format('YYYY-MM-DD HH:mm:ss') : '--';
      };

      const handleGoBack = () => {
        router.back();
      };

      const handleGoDetail = (item) => {
        // TODO: 跳转到沟通记录详情页面
        console.log('查看详情', item);
      };

      // 获取图片列表（处理逗号分隔的图片字符串）
      const getImageList = (images) => {
        if (!images) return [];
        if (typeof images === 'string') {
          return images.split(',').filter(img => img.trim());
        }
        return Array.isArray(images) ? images : [];
      };

      // 获取图片完整URL
      const getImageUrl = (img) => {
        if (!img) return '';
        // 如果已经是完整URL，直接返回
        if (img.startsWith('http://') || img.startsWith('https://')) {
          return img;
        }
        // 拼接完整路径
        return process.env.VUE_APP_BASE_API + '/file/' + img.trim();
      };

      // 预览图片
      const handlePreviewImages = (images, event) => {
        event.stopPropagation();
        const imageList = getImageList(images);
        if (imageList.length === 0) return;
        
        // 找到点击的图片索引
        const target = event.target.closest('.image-item');
        const allImageItems = event.target.closest('.images-list').querySelectorAll('.image-item');
        const clickedIndex = Array.from(allImageItems).indexOf(target);
        
        state.previewImages = imageList.map(img => getImageUrl(img));
        state.startPosition = clickedIndex >= 0 ? clickedIndex : 0;
        state.showImagePreview = true;
      };

      return {
        state,
        showDatePicker,
        dateRangeText,
        handleSearch,
        handleReset,
        onDateConfirm,
        handleClearDate,
        formatDate,
        handleGoBack,
        handleGoDetail,
        getImageList,
        getImageUrl,
        handlePreviewImages,
        maxDate: new Date(),
        minDate: new Date(new Date().setDate(new Date().getDate() - 5 * 365))
      };
    },
  });
</script> 

<style lang="scss" scoped>
.page-container {
  background: #f5f5f5;
  height: 100vh;
  overflow: hidden;
  
  .mescroll {
    height: 100%;
    overflow: auto;
    -webkit-overflow-scrolling: touch;
    
    .page-content {
      padding-bottom: 20px;
      min-height: 100%;
    }
  }
}

.filter-section {
  background: #fff;
  padding: 14px 16px 16px;
  margin-bottom: 2px;
  
  .clear-icon {
    font-size: 16px;
    color: #999;
    margin-right: 8px;
    
    &:active {
      color: #666;
    }
  }
  
  .filter-actions {
    display: flex;
    gap: 10px;
    margin-top: 14px;
    
    .search-btn {
      flex: 2;
      height: 44px;
      font-size: 16px;
    }
    
    .reset-btn {
      flex: 1;
      height: 44px;
      font-size: 16px;
    }
  }
}

.content-list {
  padding: 0 8px 20px;
}

.content-item {
  background: #fff;
  border-radius: 8px;
  margin-bottom: 8px;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.08);
  overflow: hidden;
  
  .item-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 8px 12px;
    border-bottom: 1px solid #f0f0f0;
    
    .header-name {
      font-size: 20px;
      color: #333;
      font-weight: 600;
    }
    
    .header-time {
      font-size: 13px;
      color: #999;
    }
  }
  
  .item-content {
    padding: 6px 14px;
    
    .info-row {
      display: flex;
      align-items: flex-start;
      margin-bottom: 8px;
      line-height: 20px;
      justify-content: flex-start;
      
      &:last-child {
        margin-bottom: 0;
      }
      
      .info-label {
        color: #666;
        font-size: 14px;
        flex-shrink: 0;
      }
      
      .info-value {
        color: #333;
        font-size: 14px;
        flex: 1;
        word-break: break-word;
        text-align: left;
        
        &.highlighted {
          color: #1989fa;
        }
      }
      
      &.content-row {
        flex-direction: column;
        margin-bottom: 10px;
        
        .info-label {
          margin-bottom: 6px;
          font-size: 14px;
          color: #666;
        }
        
        .content-text {
          background: #f8f9fb;
          padding: 8px 10px;
          border-left: 3px solid #1989fa;
          border-radius: 2px;
          font-size: 14px;
          color: #333;
          line-height: 1.5;
          word-break: break-word;
        }
      }
    }
    
    .item-images {
      margin: 10px 0 0;
      padding-top: 10px;
      border-top: 1px solid #f0f0f0;
      
      .images-label {
        font-size: 14px;
        color: #666;
        margin-bottom: 8px;
      }
      
      .images-list {
        display: flex;
        flex-wrap: wrap;
        gap: 8px;
        
        .image-item {
          width: 80px;
          height: 80px;
          border-radius: 4px;
          overflow: hidden;
          background: #f5f5f5;
          
          :deep(.van-image) {
            width: 100%;
            height: 100%;
          }
          
          :deep(.van-image__img) {
            width: 100%;
            height: 100%;
            object-fit: cover;
          }
        }
      }
    }
  }
  
  .item-footer {
    display: flex;
    align-items: center;
    justify-content: flex-end;
    padding: 8px 14px;
    border-top: 1px solid #f0f0f0;
    color: #1989fa;
    font-size: 14px;
    cursor: pointer;
    
    .view-detail {
      user-select: none;
    }
  }
}

:deep(.van-field__label) {
  min-width: 90px;
  color: #333;
  font-size: 15px;
}

:deep(.van-field__control) {
  font-size: 15px;
}

:deep(.van-field) {
  padding: 12px 16px;
}
</style>

